import { getLocale, setLocale, type Locale } from '../lib/i18n'
import { Flag } from './Icon'

/**
 * English / Puerto Rican Spanish.
 *
 * A visible toggle rather than a settings page, because the person who needs it
 * is the one who cannot read the settings page. It shows BOTH flags with the
 * inactive one dimmed, so what it does is obvious before it is pressed — a
 * single flag is ambiguous about whether it shows the current language or the
 * one you would switch to, and people get that wrong about half the time.
 *
 * `flagsOnly` is the header version for people who are not signed in yet: two flags and no words, small enough for a
 * phone header. Signed-in people change language on the Account page instead.
 *
 * Changing locale reloads. The alternative is threading a locale through every
 * component and re-rendering the tree, for a control most people press once,
 * ever; a reload is honest and costs nothing on a static bundle.
 */
export default function LanguageToggle({ onChange, compact = false, flagsOnly = false }: { onChange?: () => void; compact?: boolean; flagsOnly?: boolean }) {
  const current = getLocale()

  const pick = (next: Locale) => {
    if (next === current) return
    setLocale(next)
    if (onChange) onChange()
    else window.location.reload()
  }

  return (
    <div className={compact ? 'lang compact' : flagsOnly ? 'lang flags-only' : 'lang'} role="group" aria-label="Language / Idioma">
      {(['es-PR', 'en'] as const).map((loc) => (
        <button
          key={loc}
          onClick={() => pick(loc)}
          aria-pressed={current === loc}
          aria-label={loc === 'en' ? 'English' : 'Español'}
          title={loc === 'en' ? 'English' : 'Español'}
          className={current === loc ? 'on' : ''}
        >
          {compact ? null : <Flag locale={loc} size={24} />}
          {flagsOnly ? null : <span>{compact ? (loc === 'en' ? 'EN' : 'ES') : loc === 'en' ? 'English' : 'Español'}</span>}
        </button>
      ))}
    </div>
  )
}
